import React from 'react';
import ReactDOM from 'react-dom';

import { BrowserRouter } from 'react-router-dom';
import { Provider } from 'react-redux';
import thunk from 'redux-thunk';
import { createStore, applyMiddleware } from 'redux';
// import { createLogger } from 'redux-logger';
import rootReducer from './rootReducer';
import queryString from 'query-string';
import * as serviceWorker from './serviceWorker';
import axios from 'axios';
import AuthRoutes from './authRoutes';
import UnAuthorizedUserError from 'components/unAuthorizedUserError';
import UserInactiveError from 'components/userInactiveError';
import { saveState, loadState } from './redux_localstorage';
import throttle from 'lodash/throttle';
import HistoryChange from './historyChange';
import { clearAuthStorage } from './utils/theme';

import * as Sentry from '@sentry/react';
import { Integrations } from '@sentry/tracing';
import { CaptureConsole } from '@sentry/integrations';
import { BrowserTracing } from '@sentry/tracing/dist/browser';


// const logger = createLogger();`
const persistedStore = loadState();
const store = createStore(rootReducer, persistedStore, applyMiddleware(thunk));
//sync specific stores to localstorage
store.subscribe(
  throttle(() => {
    // console.log("persisting redux store filterSidebarValue..", JSON.stringify(store.getState().filterSidebarValue))
    saveState({
      filterSidebarValue: store.getState().filterSidebarValue,
    });
  }, 1000)
);

// Auth is cookie-based now: Google, email (OTP) and the new Slack sign-in all
// set httpOnly cookies, and troopr-api's authenticate() reads the access-token
// cookie (the Bearer header is optional). The legacy localStorage `token` is
// retired — a stale one is actively harmful: only the OLD Slack sign-in
// (/auth/slack/access) still wrote it, and its presence makes authRoutes take
// the Bearer path and bounce into /v2 where that flow never set a cookie
// session → an infinite /v2 ⇄ /troopr/access redirect loop. Drop it on boot so
// the cookie is the single source of truth (cookie-authed users are unaffected
// since troopr-api falls back to the cookie; a pure-Bearer session simply
// re-logs in via the cookie flow), and strip any legacy ?token= from the URL
// without storing it.
try {
  localStorage.removeItem('token');
} catch (e) {
  /* ignore */
}
if (window.location.search) {
  const qs = queryString.parse(window.location.search);
  if (qs.token) {
    let url = `https://${window.location.hostname}${window.location.pathname}`;
    if (qs.jira_login_type && qs.jira_type) {
      url += `?jira_login_type=${qs.jira_login_type}&jira_type=${qs.jira_type}`;
    }
    window.location.href = url;
  }
}

// Hardcoded to default/light theme for trooprbot-ui:
localStorage.setItem('theme', 'default');
localStorage.setItem('troopr-theme', 'default');
if (typeof document !== 'undefined' && document.documentElement) {
  document.documentElement.classList.remove('dark');
}

if (!localStorage.getItem('theme')) {
  localStorage.setItem('theme', 'default');
}

if (window.location.host.includes('localhost'))
  axios.defaults.baseURL = 'https://app-stage.troopr.io/';
// Send httpOnly auth cookies with every request (new Enjo-style cookie auth).
// Harmless for the legacy Bearer flow, which keeps working in parallel.
axios.defaults.withCredentials = true;
axios.interceptors.request.use((config) => {
  config.headers['X-Frame-Options'] = 'SAMEORIGIN';
  config.headers['wId'] = localStorage.getItem('userCurrentWorkspaceId');
  const token = localStorage.getItem('token');
  if (token && !config.headers['Authorization'] && !config.headers['authorization']) {
    config.headers['Authorization'] = `Bearer ${token}`;
  }
  return config;
});
axios.interceptors.response.use(
  async (response) => {
    response.headers['X-Frame-Options'] = 'SAMEORIGIN';

    // /api/auth/me answers 200 {success:false} — never a throw — whenever the
    // short-lived access cookie is missing, including the common "idle tab"
    // case where the 7-day refresh cookie is still perfectly valid. Handled
    // HERE (the shared interceptor) rather than in whichever component calls
    // fetchMe, so every caller gets this resilience automatically instead of
    // each needing its own refresh-fallback logic. Only one refresh attempt,
    // guarded by the same _retry flag the 401 branch below uses — if the
    // session is genuinely gone this must not loop.
    const url = String((response.config && response.config.url) || '');
    if (
      url.includes('/api/auth/me') &&
      response.data &&
      response.data.success === false &&
      response.config &&
      !response.config._retry
    ) {
      response.config._retry = true;
      try {
        await axios.post('/bot/api/auth/refresh', {}, { withCredentials: true });
        return axios(response.config);
      } catch (e) {
        // Refresh genuinely failed — fall through and return the original
        // {success:false} response as-is; the caller's existing "not signed
        // in" handling already covers this.
      }
    }
    return response;
  },
  async (error) => {
    const config = error && error.config;
    const status = error && error.response && error.response.status;
    const data = error && error.response && error.response.data;
    const url = String((config && config.url) || '');

    const isAuthPath =
      window.location.pathname.startsWith('/troopr/access') ||
      window.location.pathname.startsWith('/login') ||
      window.location.pathname.startsWith('/signup') ||
      window.location.pathname.startsWith('/verification');

    // Deactivated / unauthorized users get their dedicated friendly pages
    // (restored — the auth rewrite dropped these branches, leaving such users
    // on a blank/generic screen). Matched on the backend error code regardless
    // of status, guarded against redirect loops and auth pages.
    if (data && !isAuthPath) {
      if (
        data.error === 'user_inactive' &&
        window.location.pathname !== '/user_inactive'
      ) {
        window.location.href = '/user_inactive';
        return Promise.reject(error);
      }
      // user_unauthorized WITHOUT a WORKSPACE_MEMBERSHIP type = "no access to
      // this resource" → friendly page. The MEMBERSHIP variant is a genuine
      // session failure, handled by the 403 block below (refresh + login).
      if (
        data.error === 'user_unauthorized' &&
        data.type !== 'WORKSPACE_MEMBERSHIP' &&
        window.location.pathname !== '/user_unauthorized'
      ) {
        window.location.href = '/user_unauthorized';
        return Promise.reject(error);
      }
    }

    // New cookie-session flow: on a 401, try a one-time token refresh and
    // retry the original request. Skipped for the auth endpoints themselves.
    // Exception: skip refresh if errorType is 'no_wid' (missing workspace context).
    if (
      status === 401 &&
      config &&
      !config._retry &&
      !url.includes('/api/auth')
    ) {
      const isNoWid = data && data.errorType === 'no_wid';
      if (!isNoWid) {
        config._retry = true;
        try {
          await axios.post('/bot/api/auth/refresh', {}, { withCredentials: true });
          return axios(config);
        } catch (e) {
          // Only a definitive 401/403 from the refresh endpoint means the
          // session is genuinely gone. A network blip / 5xx during refresh
          // (backend now returns 503 for transient errors, keeping the
          // refresh cookie intact) is NOT a logout — surface the original
          // failure and let the next action retry.
          const refreshStatus = e && e.response && e.response.status;
          if (refreshStatus === 401 || refreshStatus === 403) {
            clearAuthStorage();
            window.location.href = '/troopr/access';
          }
          return Promise.reject(error);
        }
      }
      // no_wid: let the caller handle it (request simply lacked wId header)
      return Promise.reject(error);
    }

    // ── 403: only hard-logout for genuine WORKSPACE_MEMBERSHIP auth failures ──
    // A "genuine" failure means the backend explicitly rejected the session
    // (error === 'user_unauthorized', type === 'WORKSPACE_MEMBERSHIP') AND
    // it was NOT a configuration/wId issue (those are now 401s from the backend).
    if (status === 403 && !isAuthPath && !url.includes('/api/auth')) {
      const isSessionFailure =
        data &&
        data.error === 'user_unauthorized' &&
        data.type === 'WORKSPACE_MEMBERSHIP';

      if (isSessionFailure) {
        // Legacy endpoints may answer an EXPIRED access cookie with this same
        // 403 shape instead of a refreshable 401. Try one refresh + replay
        // before treating it as a genuine rejection.
        if (config && !config._retry) {
          config._retry = true;
          try {
            await axios.post('/bot/api/auth/refresh', {}, { withCredentials: true });
            return axios(config);
          } catch (e) {
            /* refresh failed — fall through to redirect */
          }
        }
        localStorage.removeItem('userCurrentWorkspaceId');
        window.location.href = '/troopr/access';
      }
      return Promise.reject(error);
    }

    // If a 401 falls through here (already retried and failed refresh),
    // then the session is genuinely gone. Redirect to login.
    if (status === 401 && !isAuthPath && !url.includes('/api/auth')) {
      clearAuthStorage();
      window.location.href = '/troopr/access';
      return Promise.reject(error);
    }


    return Promise.reject(error)
  }
);

// Sentry
Sentry.init({
  dsn: process.env.REACT_APP_SENTRY_UI,
  integrations: [
    new BrowserTracing(),
    new CaptureConsole({
      levels: ['error'],
    }),
  ],
  beforeSend(event) {
    if (
      event &&
      (!event.level ||
        event.level !== 'error' ||
        (event.message && event.message.startsWith('Warning')))
    ) {
      return null;
    }
    if (event && event.tags) {
      event.tags = {
        workspaceId: localStorage.getItem('userCurrentWorkspaceId'),
        userId: localStorage.getItem('trooprUserId'),
        ...event.tags,
      };
    }
    if (
      event &&
      event.exception &&
      event.exception.values &&
      event.exception.values.length > 0 &&
      event.exception.values[0] &&
      event.exception.values[0].value
    ) {
      event.exception.values[0].value = `${event.exception.values[0].value || ''
        } (workspaceId:${localStorage.getItem('userCurrentWorkspaceId') || ''
        } user_id:${localStorage.getItem('trooprUserId')}) `;
    }
    return event;
  },
  beforeBreadcrumb(breadcrumb, hint) {
    return breadcrumb.level !== 'warning' ? breadcrumb : null;
  },
  tracesSampleRate: 1.0,
});

ReactDOM.render(
  window.location.pathname === '/user_unauthorized' ? (
    <Provider store={store}>
      <UnAuthorizedUserError />
    </Provider>
  ) : window.location.pathname === '/user_inactive' ? (
    <Provider store={store}>
      <UserInactiveError />
    </Provider>
  ) : (
    <Provider store={store}>
      <BrowserRouter>
        <HistoryChange>
          <AuthRoutes />
        </HistoryChange>
      </BrowserRouter>
    </Provider>
  ),
  document.getElementById('root')
);

serviceWorker.unregister();
